import { lazy, Suspense } from "react";
import { useLocation } from "react-router-dom";
import type { TopicId } from "../help/helpTopics.tsx";
import { ErrorBoundary } from "./ErrorBoundary.tsx";
import { ModalDialog } from "./ModalDialog.tsx";

// The help text is its own download. AppShell fetches it once the page is
// idle, so Help usually opens instantly and still works if the connection
// drops later.
export const loadHelpCenter = () => import("../help/HelpCenter.tsx");
const HelpCenter = lazy(loadHelpCenter);

/** Help opens on the topic for the page you are on. */
function topicForPath(path: string): TopicId {
  if (path.startsWith("/train/")) return "coach";
  if (path.startsWith("/opening/")) return "theory";
  if (path.startsWith("/openings")) return "openings";
  if (path.startsWith("/progress")) return "progress";
  return "start";
}

export function HelpModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation();
  return (
    <ModalDialog
      open={open}
      onClose={onClose}
      labelledBy="helpTitle"
      className="help-modal"
      id="helpModal"
    >
      <article className="help-inner">
        <header className="help-header">
          <div>
            <p className="eyebrow">Help</p>
            <h2 id="helpTitle">How The First 15 Moves works</h2>
          </div>
          <button className="help-close" type="button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        {open ? (
          // A failed download (offline, or a tab left open across an update)
          // stays inside the dialog; browsers only retry it after a reload.
          <ErrorBoundary
            fallback={
              <div className="help-loading" role="alert">
                <p>
                  Help couldn’t load. Check your connection, then reload the page. Saved progress is
                  kept.
                </p>
                <div className="help-retry">
                  <button
                    className="btn-primary btn-sm"
                    type="button"
                    onClick={() => window.location.reload()}
                  >
                    Reload page
                  </button>
                </div>
              </div>
            }
          >
            <Suspense fallback={<p className="help-loading">Loading help…</p>}>
              <HelpCenter initialTopic={topicForPath(pathname)} />
            </Suspense>
          </ErrorBoundary>
        ) : null}
        <footer className="help-footer">
          <button className="btn-primary btn-sm" type="button" onClick={onClose}>
            Close help
          </button>
        </footer>
      </article>
    </ModalDialog>
  );
}
